import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, KeyRound, MessageSquare, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import AuthStepShell from '../auth-step-shell';
import { useLoginFlowVariant } from '../variant';

interface AuthMethodsStepProps {
    email: string;
    onEditEmail: () => void;
    onSelectPassword: () => void;
    onSelectGetCode: () => void;
    onBack: () => void;
    /** Hide the Passkey authentication button */
    showPasskeyAuth?: boolean;
    /** Hide the Password authentication button */
    showPasswordAuth?: boolean;
    /** True while a single-channel OTP send is in flight */
    isSendingCode?: boolean;
    /** Error from an immediate OTP send (single-channel flow) */
    sendCodeError?: string;
}

export default function AuthMethodsStep({
    email,
    onEditEmail,
    onSelectPassword,
    onSelectGetCode,
    onBack,
    showPasskeyAuth = false,
    showPasswordAuth = true,
    isSendingCode = false,
    sendCodeError,
}: AuthMethodsStepProps): ReactElement {
    const { t } = useTranslation('login');
    const isPage = useLoginFlowVariant() === 'page';

    const methodButtonClass = cn(
        'w-full flex items-center gap-4 p-4 bg-secondary text-left cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed',
        isPage
            ? 'rounded-ui p-4 sm:p-5 hover:bg-secondary/70 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors'
            : 'rounded-xs'
    );

    return (
        <AuthStepShell
            heading={t('sheet.signIntoAccount', { defaultValue: 'Sign into your account' })}
            onBack={onBack}
            step={2}>
            <div className="mt-8 flex gap-3">
                <span>{email}</span>
                <button
                    type="button"
                    className="cursor-pointer font-medium text-foreground underline underline-offset-2"
                    onClick={onEditEmail}>
                    {t('sheet.edit', { defaultValue: 'Edit' })}
                </button>
            </div>

            <div className="mt-6 flex flex-col gap-4">
                {showPasskeyAuth ? (
                    <button
                        type="button"
                        className={cn(methodButtonClass, 'opacity-60')}
                        onClick={() => {
                            /* TODO: Implement Passkey login */
                        }}>
                        <KeyRound className="size-5 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground">
                                {t('sheet.passkey', { defaultValue: 'Passkey' })}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {t('sheet.passkeySubtitle', { defaultValue: 'Face ID, fingerprint, PIN' })}
                            </p>
                        </div>
                        <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
                    </button>
                ) : null}

                <button type="button" className={methodButtonClass} onClick={onSelectGetCode} disabled={isSendingCode}>
                    <MessageSquare className="size-5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">
                            {isSendingCode
                                ? t('sheet.sendingCode', { defaultValue: 'Sending...' })
                                : t('sheet.getCode', { defaultValue: 'Get a code' })}
                        </p>
                    </div>
                    <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
                </button>

                {sendCodeError ? (
                    <div className="rounded bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
                        {sendCodeError}
                    </div>
                ) : null}

                {showPasswordAuth ? (
                    <button type="button" className={methodButtonClass} onClick={onSelectPassword}>
                        <Lock className="size-5 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground">
                                {t('sheet.password', { defaultValue: 'Password' })}
                            </p>
                        </div>
                        <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
                    </button>
                ) : null}
            </div>
        </AuthStepShell>
    );
}
